import { rem } from 'polished'
import { memo, useCallback, useContext, useMemo, type MouseEventHandler } from 'react'
import { useIntl } from 'react-intl'
import styled from 'styled-components'

import BaseClickable from 'components/Common/Clickable/BaseClickable'
import LineAngleDownIcon from 'LuxKit/Icons/line/LineAngleDownIcon'
import LineCheckIcon from 'LuxKit/Icons/line/LineCheckIcon'
import BodyText from 'LuxKit/Typography/BodyText'
import Heading from 'LuxKit/Typography/Heading'
import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'
import { mediaQueryUp } from 'components/utils/breakpoint'
import ModalContext from 'contexts/ModalContext'
import { sortByAll, type SortField } from 'lib/array/arrayUtils'
import getPropertyIcon from 'lib/offer/getPropertyIcon'
import PropertyStructuredFacilitiesModal from './PropertyStructuredFacilitiesModal'

const MobileFacilityGroups = styled.div`
  ${mediaQueryUp.tablet} {
    display: none;
  }
`

const MobileFacilityGroup = styled(BaseClickable)`
  display: flex;
  align-items: center;
  gap: ${rem(8)};
  width: 100%;
  min-height: ${rem(40)};
  padding: ${rem(10)} 0;
  border: 0;
  border-bottom: 1px solid ${props => props.theme.palette.neutral.surface.lightForeground};
  background-color: transparent;
  color: inherit;
  text-align: start;
`

const MobileFacilityGroupName = styled(BodyText)`
  flex: 1;
`

const DesktopFacilityGroups = styled.div`
  display: none;
  column-gap: ${rem(32)};

  > * + * {
    margin-top: ${rem(8)};
  }

  ${mediaQueryUp.tablet} {
    display: block;
    column-count: 2;
  }

  ${mediaQueryUp.desktop} {
    column-count: 3;
  }
`

const FacilityGroup = styled.div`
  break-inside: avoid;
`

const GroupHeading = styled(BodyText)`
  display: flex;
  align-items: center;
  gap: ${rem(8)};
  padding: ${rem(8)} 0;
`

const Facilities = styled.ul`
  list-style-type: none;
  margin: ${rem(8)} 0 0;

  > * + * {
    margin-top: ${rem(8)};
  }
`

const Facility = styled.li`
  display: flex;
  align-items: center;
  gap: ${rem(8)};
  padding: ${rem(2)} 0;
  min-width: 0;
`

const UNORDERED_DISPLAY_ORDER = Number.MAX_SAFE_INTEGER

const STRUCTURED_FACILITY_ITEM_SORT_FIELDS: Array<SortField<App.StructuredFacilityItem>> = [
  { selector: facility => facility.displayOrder ?? UNORDERED_DISPLAY_ORDER, direction: 'asc' },
  { selector: facility => facility.name, direction: 'asc' },
]

const STRUCTURED_FACILITY_GROUP_SORT_FIELDS: Array<SortField<App.StructuredFacilityGroup>> = [
  { selector: facilityGroup => facilityGroup.group.displayOrder ?? UNORDERED_DISPLAY_ORDER, direction: 'asc' },
  { selector: facilityGroup => facilityGroup.group.name, direction: 'asc' },
]

interface Props {
  showHeading?: boolean
  structuredFacilities: Array<App.StructuredFacilityGroup>
}

function PropertyStructuredFacilities({ showHeading = true, structuredFacilities }: Props) {
  const intl = useIntl()
  const showModal = useContext(ModalContext)
  const sortedStructuredFacilities = useMemo(
    () => sortByAll(structuredFacilities.map(facilityGroup => ({
      ...facilityGroup,
      facilities: sortByAll(facilityGroup.facilities, STRUCTURED_FACILITY_ITEM_SORT_FIELDS),
    })), STRUCTURED_FACILITY_GROUP_SORT_FIELDS),
    [structuredFacilities],
  )

  const facilitiesTitle = intl.formatMessage({
    id: 'TdQ31JcW4K2rhT2wyPgV',
    defaultMessage: 'Facilities',
    description: 'Title for facilities section on single property page',
  })
  const showFacilityGroupModal = useCallback<MouseEventHandler<HTMLButtonElement>>((event) => {
    const facilityGroupCode = event.currentTarget.dataset.facilityGroupCode
    const facilityGroup = sortedStructuredFacilities.find(({ group }) => group.code === facilityGroupCode)

    if (facilityGroup) {
      showModal(<PropertyStructuredFacilitiesModal
        facilityGroup={facilityGroup}
        title={facilitiesTitle}
      />)
    }
  }, [facilitiesTitle, showModal, sortedStructuredFacilities])

  return <VerticalSpacer gap={12}>
    {showHeading && <Heading variant="heading4" as="h4">{facilitiesTitle}</Heading>}
    <MobileFacilityGroups>
      {sortedStructuredFacilities.map(facilityGroup => {
        const GroupIcon = getPropertyIcon(facilityGroup.group.iconName)

        return <MobileFacilityGroup
          aria-haspopup="dialog"
          data-facility-group-code={facilityGroup.group.code}
          key={facilityGroup.group.code}
          onClick={showFacilityGroupModal}
        >
          <GroupIcon size="S" />
          <MobileFacilityGroupName variant="medium" weight="bold">
            {facilityGroup.group.name}
          </MobileFacilityGroupName>
          <LineAngleDownIcon size="M" />
        </MobileFacilityGroup>
      })}
    </MobileFacilityGroups>
    <DesktopFacilityGroups>
      {sortedStructuredFacilities.map(facilityGroup => {
        const GroupIcon = getPropertyIcon(facilityGroup.group.iconName)

        return <FacilityGroup key={facilityGroup.group.code}>
          <GroupHeading variant="medium" weight="bold">
            <GroupIcon size="S" />
            {facilityGroup.group.name}
          </GroupHeading>
          <Facilities>
            {facilityGroup.facilities.map(facility => (
              <Facility key={facility.code}>
                <LineCheckIcon size="S" />
                <BodyText variant="small">{facility.name}</BodyText>
              </Facility>
            ))}
          </Facilities>
        </FacilityGroup>
      })}
    </DesktopFacilityGroups>
  </VerticalSpacer>
}

export default memo(PropertyStructuredFacilities)
